import { forwardRef, useRef } from 'react'
import { useLocale } from '@react-aria/i18n'
import { TimeFieldStateOptions, useTimeFieldState, TimeFieldState } from '@react-stately/datepicker'
import { TimeValue, useTimeField } from '@react-aria/datepicker'
import { useOverlayTrigger } from '@react-aria/overlays'
import { DateSegment } from './DateSegment'
import Styles from './TimePicker.module.scss'
import cx from 'classnames'
import { ChevronDown, ChevronDown2, Clock1 } from 'blaze-ui/icons'
import { useOverlayTriggerState } from '@react-stately/overlays'
import { mergeProps } from '@react-aria/utils'
import { Popover } from 'components/Popover/Popover'
import {
  FloatingPortal,
  autoUpdate,
  flip,
  inline,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom-interactions'
import { Time } from '@internationalized/date'
import { DateValue } from '@react-aria/calendar'
import { Text, TextVariant } from 'blaze-ui/Text/Text'

interface TimePickerPopoverProps {
  state: TimeFieldState
}

interface SimplifiedTimePickerPopoverProps extends TimePickerPopoverProps {
  onSelect: () => void
}

function getTimeZoneAbbreviation() {
  const date = new Date()
  const timeZoneName = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).format(date)
  return timeZoneName.split(' ')[1]
}

export const TimePickerPopover = forwardRef<HTMLDivElement, TimePickerPopoverProps>(({ state }, ref) => {
  return (
    <div className={Styles.popover} ref={ref}>
      <div className={Styles.columns}>
        <div className={Styles.column}>
          {[...Array(12)].map((_, index) => (
            <div
              key={index}
              onMouseDown={(e) => {
                e.preventDefault()
              }}
              onClick={(e) => {
                state.setSegment('hour', index + 1)
              }}
              className={cx(
                Styles.item,
                'ignore-click-outside',
                state.segments.find(
                  (segment) =>
                    segment.type === 'hour' && segment.value != null && (segment.value % 12 || 12) === index + 1,
                )
                  ? Styles.selected
                  : '',
              )}
            >
              {index + 1}
            </div>
          ))}
        </div>
        <div className={Styles.column}>
          {[0, 15, 30, 45].map((minute) => (
            <div
              key={minute}
              onMouseDown={(e) => {
                e.preventDefault()
              }}
              onClick={() => state.setSegment('minute', minute)}
              className={cx(
                Styles.item,
                'ignore-click-outside',
                state.segments.find((segment) => segment.type === 'minute' && segment.value === minute)
                  ? Styles.selected
                  : '',
              )}
            >
              {minute === 0 ? '00' : minute}
            </div>
          ))}
        </div>
        <div className={Styles.column}>
          {['AM', 'PM'].map((period) => (
            <div
              key={period}
              onMouseDown={(e) => {
                e.preventDefault()
              }}
              onClick={() => state.setSegment('dayPeriod', period === 'AM' ? 0 : 12)}
              className={cx(
                Styles.item,
                'ignore-click-outside',
                state.segments.find(
                  (segment) => segment.type === 'dayPeriod' && segment.value === (period === 'AM' ? 0 : 12),
                )
                  ? Styles.selected
                  : '',
              )}
            >
              {period}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
})

type TimePickerProps = Partial<TimeFieldStateOptions<TimeValue>> & {
  onVisibilityChange?: (visible: boolean) => void
  fullWidth?: boolean
  size?: 'md' | 'lg'
  labelVariant?: TextVariant
  labelColor?: string
  withIcon?: boolean
}

interface NativeTimePickerProps {
  value?: TimeValue
  onChange: (value: TimeValue) => void
  showIcon?: boolean
  showTimezone?: boolean
  showChangeText?: boolean
  isAPMobileWeb?: boolean
}

export function NativeTimePicker({
  value,
  onChange,
  showIcon = false,
  showTimezone = false,
  showChangeText = false,
  isAPMobileWeb = false,
}: NativeTimePickerProps) {
  const dayPeriod = value?.hour && value.hour >= 12 ? 'PM' : 'AM'
  const displayHour = value?.hour ? value.hour % 12 || 12 : 12
  const displayValue = value
    ? `${displayHour}:${value.minute.toString().padStart(2, '0')} ${dayPeriod}`
    : 'Select a time'

  const timezone = showTimezone && !!value ? getTimeZoneAbbreviation() : ''

  return (
    <label className={cx(Styles.nativeTimePicker, { [Styles.apMobileWeb]: isAPMobileWeb })}>
      {showIcon && <Clock1 />}
      <span className={Styles.displayValue}>
        {displayValue}
        {timezone && ` ${timezone}`}
      </span>
      {showChangeText && <span className={Styles.changeTimeText}>Change Time</span>}
      <input
        alt="Select time"
        type="time"
        style={{ position: 'absolute', opacity: 0, top: 0, left: 0, width: '100%', height: '100%' }}
        className={Styles.timeInput}
        value={
          value ? `${value.hour.toString().padStart(2, '0')}:${value.minute.toString().padStart(2, '0')}` : undefined
        }
        onChange={(e) => {
          const [hour, minute] = e.target.value.split(':').map((num) => parseInt(num, 10))
          onChange(new Time(hour, minute))
        }}
      />
      <ChevronDown2 size={12} color="var(--dark-25)" />
    </label>
  )
}

export function TimePicker({
  fullWidth = true,
  labelVariant = 'metadata',
  labelColor = 'var(--dark-60)',
  size = 'lg',
  withIcon = true,
  ...props
}: TimePickerProps) {
  const { locale } = useLocale()
  const state = useTimeFieldState({ ...props, locale })
  const inputRef = useRef<HTMLInputElement>(null)

  const overlayState = useOverlayTriggerState({
    onOpenChange(isOpen) {
      props.onVisibilityChange?.(isOpen)
    },
  })

  const { triggerProps, overlayProps } = useOverlayTrigger({ type: 'dialog' }, overlayState, inputRef)

  const { labelProps, fieldProps } = useTimeField(
    {
      onBlur: (e) => {
        handleBlur()
      },
      ...props,
    },
    state,
    inputRef,
  )

  const snapToNearestQuarterHour = (minute: number) => {
    const nearestQuarter = Math.round(minute / 15) * 15
    return nearestQuarter % 60
  }

  const { x, y, reference, floating } = useFloating({
    placement: 'bottom',
    strategy: 'fixed',
    middleware: [offset(4), inline(), flip(), shift({ padding: 4 })],
    whileElementsMounted: (reference, floating, update) => {
      return autoUpdate(reference, floating, update, {
        animationFrame: true,
      })
    },
  })

  const handleBlur = () => {
    const currentMinute = state.segments.find((segment) => segment.type === 'minute')?.value ?? 0
    const snappedMinute = snapToNearestQuarterHour(currentMinute)
    state.setSegment('minute', snappedMinute)
    overlayState.close()
  }

  return (
    <div className={cx(Styles.timePicker, fullWidth && Styles.fullWidth)} ref={reference}>
      <span {...labelProps}>
        <Text variant={labelVariant} color={labelColor}>
          {props.label}
        </Text>
      </span>
      <div
        {...mergeProps(fieldProps, triggerProps)}
        className={cx(Styles.inputField, Styles[size])}
        ref={inputRef}
        onClick={overlayState.open}
      >
        {withIcon && <Clock1 />}
        <div className={Styles.segments}>
          {state.segments.map((segment, i) => (
            <DateSegment key={i} segment={segment} state={state} />
          ))}
          <div className={Styles.timezone}>{getTimeZoneAbbreviation()}</div>
        </div>
        <ChevronDown size={12} />
      </div>
      {overlayState.isOpen && (
        <FloatingPortal id="timepicker-ui-root">
          <Popover
            {...overlayProps}
            style={{
              position: 'fixed',
              top: y || 0,
              left: x || 0,
              pointerEvents: 'auto',
            }}
            backdropClass={Styles.backdrop}
            isOpen={overlayState.isOpen}
            onClose={overlayState.close}
            ref={floating}
            className={Styles.overlay}
          >
            <TimePickerPopover state={state} />
          </Popover>
        </FloatingPortal>
      )}
    </div>
  )
}

export const SimplifiedTimePickerPopover = ({ state, onSelect }: SimplifiedTimePickerPopoverProps) => {
  const non24Hour = (hour: number) => {
    return hour > 12 ? hour % 12 : hour
  }

  const amOrPm = (hour: number) => {
    return hour > 11 ? 'pm' : 'am'
  }

  return (
    <>
      {[...Array(24)].map((_, hourIndex) => {
        const hour = non24Hour(hourIndex)

        return (
          <>
            {[...Array(4)].map((_, minIndex) => {
              const min = (minIndex % 4) * 15
              const currentValue = `${hour === 0 ? 12 : hour}:${min === 0 ? `0${min}` : min}${amOrPm(hourIndex)}`

              const selectedHour = non24Hour(state.timeValue.hour)
              const selectedMin = state.timeValue.minute === 0 ? '00' : state.timeValue.minute
              const selectedValue = `${selectedHour === 0 ? 12 : selectedHour}:${selectedMin}${amOrPm(
                state.timeValue.hour,
              )}`

              return (
                <div
                  key={currentValue}
                  onMouseDown={(e) => {
                    e.preventDefault()
                  }}
                  onClick={(e) => {
                    state.setValue({ ...state.value, hour: hourIndex, minute: min } as DateValue)
                    onSelect()
                  }}
                  className={cx(Styles.simpleItem, { [Styles.selected]: currentValue === selectedValue })}
                >
                  {currentValue}
                </div>
              )
            })}
          </>
        )
      })}
    </>
  )
}

export function SimplifiedTimePicker({
  fullWidth = true,
  size,
  labelVariant = 'metadata',
  labelColor = 'var(--dark-60)',
  ...props
}: TimePickerProps) {
  const { locale } = useLocale()
  const state = useTimeFieldState({ ...props, locale })
  const inputRef = useRef<HTMLInputElement>(null)

  const overlayState = useOverlayTriggerState({
    onOpenChange(isOpen) {
      props.onVisibilityChange?.(isOpen)
    },
  })

  const { triggerProps, overlayProps } = useOverlayTrigger({ type: 'dialog' }, overlayState, inputRef)

  const { labelProps, fieldProps } = useTimeField(props, state, inputRef)

  const { x, y, reference, floating } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [offset(4), inline(), flip(), shift({ padding: 4 })],
    whileElementsMounted: (reference, floating, update) => {
      return autoUpdate(reference, floating, update, {
        animationFrame: true,
      })
    },
  })

  return (
    <div className={cx(Styles.timePicker, fullWidth && Styles.fullWidth)} ref={reference}>
      <span {...labelProps}>
        <Text variant={labelVariant} color={labelColor}>
          {props.label}
        </Text>
      </span>
      <div
        {...mergeProps(fieldProps, triggerProps)}
        className={Styles.inputField}
        ref={inputRef}
        onClick={overlayState.open}
      >
        <Clock1 />
        <div className={Styles.segments}>
          {state.segments.map((segment, i) => (
            <span key={segment.text}>{segment.text.toLowerCase()}</span>
          ))}
          <div className={Styles.timezone}>{getTimeZoneAbbreviation()}</div>
        </div>
        <ChevronDown size={12} />
      </div>
      {overlayState.isOpen && (
        <FloatingPortal id="timepicker-ui-root">
          <Popover
            {...overlayProps}
            style={{
              visibility: x === null && y === null ? 'hidden' : 'visible',
              position: 'fixed',
              top: y || 0,
              left: x || 0,
              pointerEvents: 'auto',
            }}
            backdropClass={Styles.backdrop}
            isOpen={overlayState.isOpen}
            onClose={overlayState.close}
            ref={floating}
            className={Styles.simplePopover}
            isDismissable
          >
            <SimplifiedTimePickerPopover state={state} onSelect={overlayState.close} />
          </Popover>
        </FloatingPortal>
      )}
    </div>
  )
}

export default TimePicker
